import { useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_CONFIG } from '../config/authConfig';
import { getSessionToken, clearUserSessionFully } from '../utils/sessionManager';
import { forgetCurrentAccount } from '../utils/deviceAccounts';
import { notify } from '../../notifications';

const KEEP_ALIVE_INTERVAL_MS = 25 * 1000; // 25 секунд для быстрой реакции на завершение сессии

/**
 * Хук для отслеживания текущего раздела пользователя и активности сессии.
 * - Отправляет сигнал на сервер при смене раздела (event-driven)
 * - Отправляет редкий keep-alive (раз в 3 мин) ТОЛЬКО когда вкладка активна
 * - Автоматически выбрасывает на логин, если сессия отозвана (смена пароля или выход)
 */
export const useSessionActivity = (currentSectionName?: string) => {
  const lastReportedSectionRef = useRef<string>('');
  const lastActiveTimestampRef = useRef<number>(Date.now());
  const navigate = useNavigate();

  const handleSessionRevoked = useCallback((reason?: string) => {
    // Мертвая сессия уходит из списка аккаунтов устройства; остальные аккаунты остаются
    forgetCurrentAccount();
    clearUserSessionFully();
    const alertMsg = reason === 'password_changed'
      ? 'Ваш пароль был изменен. Пожалуйста, выполните вход с новым паролем.'
      : (reason === 'expired_end_of_day' || reason === 'session_expired')
      ? 'Рабочий день завершен (сессия истекла в 23:59). Пожалуйста, выполните вход снова.'
      : 'Ваша сессия была завершена. Пожалуйста, выполните вход снова.';
    notify.warning(alertMsg, { id: 'session-revoked', duration: 10000 });
    // Экран входа покажет оставшиеся живые аккаунты, не входя в них сам
    navigate('/', { state: { skipAutoEnter: true } });
  }, [navigate]);

  const sendActivityReport = useCallback(async (section: string) => {
    const sessionToken = getSessionToken();
    if (!sessionToken) return;

    try {
      const response = await axios.post(`${API_CONFIG.AUTH_V2_URL}/activity`, {
        sessionToken,
        section
      });

      if (response.data && response.data.valid === false) {
        handleSessionRevoked(response.data.reason);
      } else {
        lastReportedSectionRef.current = section;
        lastActiveTimestampRef.current = Date.now();
      }
    } catch (err: any) {
      if (err.response?.status === 401 || err.response?.data?.code === 'SESSION_REVOKED') {
        handleSessionRevoked();
      }
    }
  }, [handleSessionRevoked]);

  // 1. Отправка отчета при изменении текущего раздела (Event-Driven)
  useEffect(() => {
    if (!currentSectionName) return;

    if (currentSectionName !== lastReportedSectionRef.current) {
      sendActivityReport(currentSectionName);
    }
  }, [currentSectionName, sendActivityReport]);

  // 2. Фоновый keep-alive только при активной вкладке
  useEffect(() => {
    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        const sectionToReport = currentSectionName || lastReportedSectionRef.current || '/';
        sendActivityReport(sectionToReport);
      }
    }, KEEP_ALIVE_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastReport = Date.now() - lastActiveTimestampRef.current;
        if (timeSinceLastReport >= 15000) {
          const sectionToReport = currentSectionName || lastReportedSectionRef.current || '/';
          sendActivityReport(sectionToReport);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentSectionName, sendActivityReport]);

  return {
    reportSection: sendActivityReport
  };
};
